import { useContext, useMemo } from 'react';
import type { ReactNode, RefObject } from 'react';
import styled from '@emotion/styled';
import css from '@emotion/css';
import type { RoutablePageConfig } from '@kizenapps/engine';
import type { CollectedFormDataResponse } from '@kizenapps/engine/react';
import { useAppPage } from '@kizenapps/engine/react';
import { usePluginFrameProxyLoaded } from 'hooks/usePluginFrameProxyLoaded';
import { PluginContext } from './context';

export type PluginViewSizing = 'fill' | 'flex-fill' | 'flex-content' | 'page';

type SizingProps = { sizing: PluginViewSizing };

const fillChildrenCss = css`
  display: flex;
  flex-direction: column;

  > * {
    flex: 1 0 auto;
  }
`;

const sizingStyles = ({ sizing }: SizingProps) => {
  switch (sizing) {
    case 'flex-fill':
      return css`
        flex: 1 1 0%;
        ${fillChildrenCss}
      `;
    case 'flex-content':
      return css`
        flex: 1 0 auto;
        height: auto;
        ${fillChildrenCss}
      `;
    case 'page':
      return css`
        height: auto;
        overflow: visible;
        position: relative;
      `;
    default:
      return null;
  }
};

const ScriptBody = styled.div<SizingProps>`
  height: 100%;
  width: 100%;
  ${sizingStyles}
`;

const HtmlBody = styled.div<SizingProps>`
  height: 100%;
  width: 100%;
  overflow: auto;
  ${sizingStyles}
`;

const Frame = styled.iframe<SizingProps>`
  height: 100%;
  width: 100%;
  border: none;
  display: block;
  ${sizingStyles}
`;

interface UsePluginViewPageArgs {
  pluginApiName: string | undefined;
  viewApiName: string | undefined;
  pages?: RoutablePageConfig[];
}

export const findPluginViewPage = (
  pages: RoutablePageConfig[],
  pluginApiName: string | undefined,
  viewApiName: string | undefined
) =>
  pages.find(
    (p) => p.plugin_api_name === pluginApiName && p.api_name === viewApiName
  );

export const usePluginViewPage = ({
  pluginApiName,
  viewApiName,
  pages,
}: UsePluginViewPageArgs): RoutablePageConfig | undefined => {
  const { routablePages } = useContext(PluginContext);

  const source = pages ?? routablePages;

  return useMemo(
    () => findPluginViewPage(source, pluginApiName, viewApiName),
    [source, pluginApiName, viewApiName]
  );
};

export interface PluginViewState {
  page: RoutablePageConfig | undefined;
  pending: boolean;
  frameLoaded: boolean;
  collectFormData: () => CollectedFormDataResponse;
}

export type PluginViewChildren = (
  body: ReactNode,
  state: PluginViewState
) => ReactNode;

export interface PluginViewProps {
  pluginApiName: string;
  viewApiName: string;
  args?: Record<string, unknown>;
  fallback?: ReactNode;
  page?: RoutablePageConfig;
  pages?: RoutablePageConfig[];
  search?: string;
  isLoading?: boolean;
  sizing?: PluginViewSizing;
  iframeTitle?: string;
  children?: PluginViewChildren;
}

export const PluginView = ({
  pluginApiName,
  viewApiName,
  args,
  fallback = null,
  page,
  pages,
  search,
  isLoading,
  sizing = 'fill',
  iframeTitle,
  children,
}: PluginViewProps) => {
  const resolvedPage = usePluginViewPage({ pluginApiName, viewApiName, pages });

  const found = page ?? resolvedPage;

  const view = useMemo(() => {
    if (!found || !args) {
      return found;
    }

    return {
      ...found,
      args: { ...found.args, ...args } as RoutablePageConfig['args'],
    };
  }, [found, args]);

  const {
    scriptUIRef,
    outputUIRef,
    scopedCss,
    sanitizedHtml,
    interactableScriptRef,
    iframeURL,
    pending,
    collectFormData,
  } = useAppPage(view, search, isLoading);

  const frameLoaded = usePluginFrameProxyLoaded(
    view?.type === 'iframe' ? pluginApiName : undefined
  );

  const state = useMemo<PluginViewState>(
    () => ({ page: view, pending, frameLoaded, collectFormData }),
    [view, pending, frameLoaded, collectFormData]
  );

  let body: ReactNode = fallback;

  if (view?.type === 'script') {
    body = (
      <>
        <ScriptBody ref={scriptUIRef as RefObject<HTMLDivElement>} sizing={sizing} />
        <style>{scopedCss}</style>
      </>
    );
  } else if (view?.type === 'html') {
    body = (
      <HtmlBody ref={interactableScriptRef as RefObject<HTMLDivElement>} sizing={sizing}>
        {sanitizedHtml ? (
          <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
        ) : null}
        <div ref={outputUIRef as RefObject<HTMLDivElement>} style={{ flex: '0 0 auto' }} />
        <style>{scopedCss}</style>
      </HtmlBody>
    );
  } else if (view?.type === 'iframe') {
    body = iframeURL ? (
      <Frame
        src={iframeURL}
        sizing={sizing}
        title={iframeTitle ?? view.name}
        name={pluginApiName}
      />
    ) : null;
  }

  return <>{children ? children(body, state) : body}</>;
};
